/**
 * Paylaşan cihazın ölçüm döngüsü (9.3.3.3 · 9.3.3.4 · D-582).
 *
 * Döngü saf tutuldu: saat, konum okuyucu ve gönderici DIŞARIDAN veriliyor.
 * Böylece "duran telefon seyrek ölçer" ve "bakan yoksa hiç ölçmez" iddiaları
 * gerçek bir GPS'e ya da ağa ihtiyaç duymadan sınanabiliyor — iddiaların
 * kendisi enerji hakkında olduğu için bunları gözle doğrulamak mümkün değil.
 */
import {
  decideSend,
  nextReadDelaySeconds,
  type Point,
} from './locationEnergy';

export type LoopDeps = {
  readPoint: () => Promise<(Point & { speed?: number | null }) | null>;
  sendPoint: (point: Point) => Promise<{ watching: boolean }>;
  /** Bir sonraki ölçüme kadar bekler; iptal edilebilir. */
  wait: (seconds: number) => Promise<void>;
  now: () => number;
};

export type LoopHandle = { stop: () => void };

/**
 * Döngü, ölçmeyi iki durumda bırakır: paylaşım kapandığında (`watching`
 * yanıtı sunucudan gelir) ve `stop()` çağrıldığında.
 *
 * Bakan yokken de tamamen susmuyor, SEYREK yokluyor: aksi hâlde biri
 * haritayı açtığında onu uyandıracak hiçbir şey olmazdı. Gerçek uyandırma
 * sessiz bildirimle gelecek (9.3.3.7); o gelene kadar bu seyrek yoklama
 * aradaki en ucuz köprü ve maliyeti kayda geçiriliyor.
 */
export const IDLE_POLL_SECONDS = 120;

export function startLiveLocationLoop(deps: LoopDeps): LoopHandle {
  let durduruldu = false;
  let sonGonderilen: Point | null = null;
  // "Hiç gönderilmedi" işareti null; sıfır DEĞİL. Sıfırı işaret olarak
  // kullanmak, saatin sıfır olamayacağını varsaymaktı ve test bunu ilk turda
  // düşürdü: ilk gönderim saat sıfırken yapıldığında ikinci nokta da
  // "nabız zamanı geldi" sanılıp gönderiliyordu.
  let sonGonderimAn: number | null = null;

  void (async () => {
    while (!durduruldu) {
      const nokta = await deps.readPoint();
      if (durduruldu) return;

      if (!nokta) {
        await deps.wait(IDLE_POLL_SECONDS);
        continue;
      }

      const gecen = sonGonderimAn === null
        ? Number.POSITIVE_INFINITY
        : (deps.now() - sonGonderimAn) / 1000;
      const karar = decideSend({ current: nokta, lastSent: sonGonderilen, secondsSinceLastSend: gecen });

      let izleniyor = true;
      if (karar === 'send') {
        try {
          const yanit = await deps.sendPoint(nokta);
          izleniyor = yanit.watching;
          sonGonderilen = nokta;
          sonGonderimAn = deps.now();
        } catch {
          // Ağ hatası paylaşımı bitirmiyor: bir sonraki turda yeniden denenir.
        }
      }
      if (durduruldu) return;

      // Bakan yoksa ölçüm seyrekleşiyor. Ayrım burada görünür hâle geliyor:
      // Maps ve Find My bu durumda da aynı sıklıkta iter.
      await deps.wait(izleniyor ? nextReadDelaySeconds(nokta.speed) : IDLE_POLL_SECONDS);
    }
  })();

  return { stop: () => { durduruldu = true; } };
}
